import { computed, watch } from 'vue'
import { useAppDataStore } from '@/stores/app-data'
import { usePredicateStore } from '@/stores/predicate-store'

/**
 * Visibility + state for the Source filter the `*Finder` element browsers offer while building
 * a character. Source matches the element's own `source` property — where it was published —
 * which is orthogonal to campaign setting, so it is meaningful for every draft, world-scoped or
 * not. (World — which settings an element is linked to — is the separate, non-competing
 * question `useCharacterWorldScope` answers for these same finders.)
 *
 * Gating on `store.character` keeps the control out of the surfaces that reuse these finders
 * without a character (the data manager's element editors): CharacterCreatorView nulls
 * `store.character` on unmount, so nothing lingers.
 *
 * The selection lives in the shared `predicateStore.sources` slot that `useElementPredicates`
 * already turns into the `source.uuid` predicate, so a source picked in one finder carries into
 * the next — deliberate, and the same slot guided setup uses. CharacterCreatorView owns the
 * reset, mirroring how CharacterSetupFlow owns it for guided setup.
 *
 * `onChange` refetches the calling browser when the selection changes. It is scoped to
 * `sources` rather than the whole predicate list because these finders already call
 * `browser.fetch()` by hand on every other filter write — watching all predicates would fetch
 * each of those twice.
 */
export function useCharacterSourceFilter(onChange?: () => void) {
    const store = useAppDataStore()
    const predicateStore = usePredicateStore()

    const visible = computed(() => !!store.character)

    // A source left in the shared slot must not light up the browser's "Clear filters"
    // affordance on a surface that is not showing the control (no character loaded).
    const active = computed(() => visible.value && predicateStore.sources.length > 0)

    // clear() is called by the browser's own "Clear filters" handler, which refetches straight
    // after — so the write it makes must not also wake the watcher, or that click fires two
    // identical requests. Only armed when a change will actually occur, so the flag is always
    // consumed by the fire it was set for.
    let suppressNext = false
    const clear = () => {
        if (predicateStore.sources.length === 0) return
        suppressNext = true
        predicateStore.sources = []
    }

    if (onChange) {
        watch(() => predicateStore.sources, () => {
            if (suppressNext) {
                suppressNext = false
                return
            }
            if (visible.value) onChange()
        }, { deep: true })
    }

    return { visible, active, clear }
}
